import React from 'react';
import { Users, UserCheck, Calendar, Clock, TrendingUp, AlertCircle } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const StaffStats = ({ staff = [], assignments = [], events = [] }) => {
  // Calculate stats
  const totalStaff = staff?.length || 0;
  const activeStaff = staff?.filter(member => member?.status === 'active')?.length || 0;
  const totalAssignments = assignments?.length || 0;
  const upcomingEvents = events?.filter(event => 
    new Date(event?.event_date) > new Date()
  )?.length || 0;

  // Calculate active assignments (future events only)
  const activeAssignments = assignments?.filter(assignment => 
    assignment?.status === 'assigned' && 
    assignment?.events?.event_date && 
    new Date(assignment?.events?.event_date) > new Date()
  )?.length || 0;

  // Calculate average assignments per staff
  const avgAssignmentsPerStaff = totalStaff > 0 ? (totalAssignments / totalStaff)?.toFixed(1) : '0';

  // Calculate staff utilization (staff with active assignments / total active staff)
  const staffWithActiveAssignments = new Set(
    assignments
      ?.filter(assignment => 
        assignment?.status === 'assigned' && 
        assignment?.events?.event_date && 
        new Date(assignment?.events?.event_date) > new Date()
      )
      ?.map(assignment => assignment?.staff_id)
  )?.size;

  const utilizationRate = activeStaff > 0 ? 
    ((staffWithActiveAssignments / activeStaff) * 100)?.toFixed(1) : '0';

  const stats = [
    {
      title: 'Total Staff',
      value: totalStaff,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      description: `${activeStaff} active`
    },
    {
      title: 'Active Assignments',
      value: activeAssignments,
      icon: Calendar,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      description: 'Current & upcoming'
    },
    {
      title: 'Upcoming Events',
      value: upcomingEvents,
      icon: Clock,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      description: 'Requiring staff'
    },
    {
      title: 'Staff Utilization',
      value: `${utilizationRate}%`,
      icon: TrendingUp,
      color: utilizationRate >= 70 ? 'text-green-600' : utilizationRate >= 50 ? 'text-yellow-600' : 'text-red-600',
      bgColor: utilizationRate >= 70 ? 'bg-green-100' : utilizationRate >= 50 ? 'bg-yellow-100' : 'bg-red-100',
      description: `${staffWithActiveAssignments}/${activeStaff} staff assigned`
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
      {stats?.map((stat, index) => {
        const Icon = stat?.icon;
        
        return (
          <div
            key={index}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center">
                  <div className={`p-2 rounded-lg ${stat?.bgColor} mr-3`}>
                    <Icon className={`w-5 h-5 ${stat?.color}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat?.title}</p>
                  </div>
                </div>
                <div className="mt-3">
                  <p className={`text-2xl font-bold ${stat?.color}`}>
                    {stat?.value}
                  </p>
                  {stat?.description && (
                    <p className="text-sm text-gray-500 mt-1">
                      {stat?.description}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {/* Additional insights */}
            {index === 3 && ( // Staff Utilization card
              (<div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center text-xs text-gray-500">
                  {utilizationRate >= 70 ? (
                    <>
                      <UserCheck className="w-3 h-3 mr-1 text-green-500" />
                      Great utilization rate
                    </>
                  ) : utilizationRate >= 50 ? (
                    <>
                      <AlertCircle className="w-3 h-3 mr-1 text-yellow-500" />
                      Moderate utilization
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-3 h-3 mr-1 text-red-500" />
                      Low utilization rate
                    </>
                  )}
                </div>
              </div>)
            )}
            {index === 0 && totalStaff > 0 && ( // Total Staff card
              (<div className="mt-4 pt-4 border-t border-gray-100">
                <div className="text-xs text-gray-500">
                  Avg. {avgAssignmentsPerStaff} assignments per staff
                </div>
              </div>)
            )}
          </div>
        );
      })}
    </div>
  );
};

export default StaffStats;